/**
 * Memorial Wall (08 §2.10, 04 §5): every ranger ever lost, newest first, as plaques - two-tone
 * bust, rank, medals, the operation and mission, the cause, the epitaph. It can be filtered by
 * operation and it can never be edited: there is no destructive control on this screen.
 * A man whose tags a later mission brought home (04 §2.3) has them marked: his medals are confirmed.
 *
 * Two walls stand here (WALL-contracts): MY COMPANY, which is this profile's and has always been
 * here, and EVERYONE, the public wall other players have chosen to put their dead on. The public
 * one is an extra: if the server says nothing, the screen falls back to your own dead without fuss.
 *
 * PHOTOS: a plaque whose man has a picture of his death is headed by it (photoView.ts); click it for
 * full size. No picture, or no photos in this copy of the game, and the plaque is as it always was.
 */

import './rosters.css';
import type { MemorialEntry } from '../../meta/types';
import type { Ctx, Screen } from './context';
import { box, button, el } from './dom';
import { publicWallView } from './memorialPublic';
import type { PictureSource } from './photoView';
import { companyPicture, photoThumb, pictureSlot } from './photoView';
import { choiceRow } from './widgets/choiceRow';
import { dossierCard } from './widgets/dossierCard';
import { frame } from './widgets/frame';
import { stamp } from './widgets/stamp';

/** The filter value that shows every operation. */
const ALL = '';

/** Which wall is on screen. */
export type WallSource = 'mine' | 'everyone';

/**
 * Newest first. `toMemorial` UNSHIFTS each name, so the profile already holds the wall newest-first
 * (`metaAftermath.test.ts`, "the wall is newest first") - this used to reverse it as well and so showed
 * the oldest man first, on a screen whose whole promise is that the most recent loss is at the top. Pure.
 */
export function wallOrder(entries: readonly MemorialEntry[], operation: string): MemorialEntry[] {
  return operation === ALL ? [...entries] : entries.filter((e) => e.operation === operation);
}

/** Distinct operation codenames, newest first. Pure. */
export function wallOperations(entries: readonly MemorialEntry[]): string[] {
  return [...new Set(entries.map((e) => e.operation))];
}

/**
 * One plaque. `yours` marks a man of your own company standing on the public wall, so a player can
 * find his own dead among everyone else's. `picture` (PHOTOS) is where the picture of his death comes
 * from - by default this company's own, by his entry's photo id; the public wall passes its own
 * address, or null. The plaque is returned at once and the picture heads it when it arrives.
 */
export function plaque(
  ctx: Ctx,
  entry: MemorialEntry,
  yours = false,
  picture: PictureSource = companyPicture(ctx, entry),
): HTMLElement {
  const card = dossierCard(entry.ranger, { describe: ctx.describe, memorial: true });
  card.classList.add('menu-plaque');
  const photo = pictureSlot('menu-plaque-photo', picture, (url, fail) => photoThumb(ctx, entry, url, fail));
  // In front of the dossier's own head: the one place a card is headed by something that is not his name.
  if (photo !== null) card.replaceChildren(photo, ...Array.from(card.childNodes));
  // Focusable so the arrow keys can walk the wall and scroll it; a plaque does nothing else.
  card.setAttribute('tabindex', '0');
  const fate = entry.fate === 'lost' ? 'LOST' : 'KIA';
  card.append(
    box(
      'menu-plaque-fate',
      stamp({ text: fate, tone: 'red' }, true),
      el('span', 'menu-plaque-operation', `OPERATION ${entry.operation}`),
    ),
    el('div', 'menu-plaque-line', entry.mission),
    el('div', 'menu-plaque-line menu-plaque-cause', entry.cause),
    el('div', 'menu-plaque-epitaph menu-prose', entry.epitaph),
  );
  if (entry.tagsRecovered === true) {
    card.append(
      box(
        'menu-plaque-tagline',
        stamp({ text: 'TAGS RECOVERED', tone: 'green' }, true),
        el('span', 'menu-small', 'MEDALS CONFIRMED'),
      ),
    );
  }
  if (yours) {
    card.classList.add('menu-plaque-yours');
    card.append(box('menu-plaque-tagline', stamp({ text: 'ONE OF YOURS', tone: 'amber' }, true)));
  }
  return card;
}

export function memorialScreen(ctx: Ctx): Screen {
  const entries = ctx.api.profile().memorial;
  // A wall this copy of the game has no server for is not offered at all, not offered and empty.
  const injected = ctx.wall ?? null;
  const port = injected !== null && injected.available() ? injected : null;
  const f = frame('memorial', 'MEMORIAL WALL');
  const count = el('span', '');
  f.info.append(count);

  const grid = el('div', 'menu-card-grid menu-scroll menu-grow');
  const back = button('BACK', () => ctx.nav.title());
  const publicWall = publicWallView(ctx, port, grid, count);

  const paintMine = (operation: string): void => {
    const shown = wallOrder(entries, operation);
    count.textContent = operation === ALL
      ? `${entries.length} NAMES`
      : `${shown.length} OF ${entries.length} NAMES`;
    grid.replaceChildren(...shown.map((entry) => plaque(ctx, entry)));
    if (shown.length === 0) {
      grid.append(el('div', 'menu-empty menu-prose', 'THE WALL IS EMPTY. KEEP IT THAT WAY.'));
    }
  };

  const choices = [{ value: ALL, label: 'ALL' }, ...wallOperations(entries).map((op) => ({ value: op, label: op }))];
  const filter = choiceRow('OPERATION', choices, ALL, paintMine);

  const repaint = (source: WallSource): void => {
    // The operation codenames are this player's own: they mean nothing against everyone else's dead.
    filter.el.hidden = source === 'everyone';
    if (source === 'everyone') void publicWall.show();
    else paintMine(filter.value());
  };
  // Without a port there is no public wall, so the row is never built: a hidden button is still a
  // button to everything that walks this screen, from the arrow keys to the tests.
  const sources = port === null ? null : choiceRow<WallSource>(
    'WALL',
    [{ value: 'mine', label: 'MY COMPANY' }, { value: 'everyone', label: 'EVERYONE' }],
    'mine',
    repaint,
  );
  repaint('mine');

  const controls = box('menu-row menu-spread', ...(sources === null ? [] : [sources.el]), filter.el, back);
  f.body.append(box('menu-col menu-grow', controls, grid));
  return { el: f.el, back: () => ctx.nav.title(), initialFocus: () => back };
}
